// Cómo se llama y cómo se pinta una CASA.
//
// POR QUÉ EXISTE
// --------------
// Desde la mig 277 la unidad de una conversación no es la persona, es la
// familia. Eso lo estrenó la bandeja del club (`ComunicacionesPage`) y estas
// tres funciones vivían dentro de ella.
//
// Ahora la pantalla del entrenador usa el MISMO modelo —decisión de Jose: «un
// entrenador no necesita saber si le escribe el padre o la madre: es la casa
// de ese alumno, y así el mismo modelo vale en los dos sitios»—, así que se
// mueven aquí en vez de copiarse.
//
// Es la misma razón por la que la clave la calcula la base y no el cliente
// (nota de la 277): el día que dos implementaciones de «cómo se llama esta
// casa» discreparan, el club vería un rótulo y el entrenador otro, sin que
// nada diera error. Una casa se llama igual desde las dos puertas.

/** Lo que hace falta saber de un miembro para rotular su casa. */
export interface MiembroDeCasa {
  id: string;
  full_name: string;
  role?: string | null;
}

/** "AP" — las iniciales de siempre, de una persona. */
export function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

/**
 * "Adidas", "Adidas y Nike", "Ana, Luis y Marta".
 *
 * Titulan los ALUMNOS, no todos los miembros: el club conoce a la casa por sus
 * hijos, y meter al tutor en el rótulo daría «Adidas, Nike y Nike Junior» —más
 * largo y peor de reconocer—. El tutor sigue DENTRO de la conversación: es de
 * quien se lee el nombre al firmar una burbuja suya y es una opción válida al
 * responder.
 *
 * Si no hubiera ningún alumno —un tutor al que se le revocaron todos los
 * vínculos— titula quien quede. Una fila sin nombre es peor que una fila rara.
 */
export function nombreDeFamilia(miembros: MiembroDeCasa[]): string {
  const alumnos = miembros.filter((m) => m.role === 'alumno');
  const titulares = alumnos.length > 0 ? alumnos : miembros;
  const nombres = titulares.map((m) => m.full_name.trim()).filter(Boolean);
  if (nombres.length === 0) return '';
  if (nombres.length === 1) return nombres[0];
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/**
 * El avatar de una familia. Con un miembro son sus iniciales de siempre; con
 * varios, la inicial de cada uno — `initials('Adidas y Nike')` daría "AY", que
 * no significa nada.
 */
export function inicialesDeFamilia(miembros: MiembroDeCasa[]): string {
  if (miembros.length <= 1) return initials(miembros[0]?.full_name ?? '');
  return miembros
    .slice(0, 3)
    .map((m) => m.full_name.trim()[0] ?? '')
    .join('')
    .toUpperCase();
}
